import { useNavigate } from 'react-router-dom';

import {
    CaseReportSubmitDocument,
    CaseReportSubmitMutationVariables,
} from '@graphql/generated/graphql';

import { normaliseEmail } from '@utils/reporterEmail';

import { getPath } from '@services/RouteService/paths';

import { useMutation } from '@apollo/client';
import {
    resetReporterDetails,
    subsidiarySiteVar,
    verifiedReporterEmailsVar,
} from '@boot/reactiveVariables';

/**
 * Router state handed to the submitted page by the guest (step 4) submission.
 *
 * The submitted page has no other way to know how the updates address ended
 * up: the mutation response carries only the case, and the reporter's own
 * data is wiped on completion (`resetReporterDetails`) precisely so nothing
 * about them outlives the flow. Router state is NOT ephemeral, though - it
 * serialises into `history.state` and survives both a reload and the session
 * history. Two things keep the address from outliving its one render: on a
 * reload the cache is empty, so the submitted page redirects without reading
 * the state; and the page strips the state from the history entry on its
 * first render, so Back cannot resurrect it either.
 */
export interface SubmittedReportLocationState {
    updatesEmailOutcome: {
        /** The confirmed updates address, or null when none was kept. */
        confirmedAddress: string | null;
    };
}

/**
 * Submits the report and navigates to the submitted page. Used by the
 * logged-in review step (no PIN) and the guest PIN step.
 */
export const useSubmitCaseReport = () => {
    const navigate = useNavigate();

    return useMutation(CaseReportSubmitDocument, {
        context: {
            notifyOnError: true,
        },
        onCompleted: (data, clientOptions) => {
            // Apollo types `variables` as loose `OperationVariables` here;
            // this mutation only ever runs with its own generated variables.
            const input = (
                clientOptions?.variables as
                    | CaseReportSubmitMutationVariables
                    | undefined
            )?.input;

            /*
             * Only the guest flow passes through the PIN step, so only it has
             * an updates-address outcome to report; a logged-in submission
             * gets updates in the portal and carries no state. Read the
             * verified list before `resetReporterDetails` wipes it. An
             * unconfirmed address is discarded server-side, so it is reported
             * as "none kept" - never echoed back as if it would be used.
             */
            const state: SubmittedReportLocationState | undefined =
                input?.pinCode
                    ? {
                          updatesEmailOutcome: {
                              confirmedAddress:
                                  input.guestEmail &&
                                  verifiedReporterEmailsVar().includes(
                                      normaliseEmail(input.guestEmail),
                                  )
                                      ? input.guestEmail
                                      : null,
                          },
                      }
                    : undefined;

            navigate(
                getPath({
                    params: { caseUuid: data.newCase.uuid },
                    path: '/new-conversation/:caseUuid/success',
                }),
                { state },
            );
            subsidiarySiteVar(undefined);
            resetReporterDetails();
        },
        onError: () => {},
    });
};
